/**
 * BalanceStats — Computation engine for the Balance Chart.
 *
 * Computes effective stats (HP, DPS, Speed, Range, Cost, Energy Sustain, AoE Factor, Power)
 * for all enemy types and robot templates. Also provides reverse-mapping functions
 * to translate chart-drag operations back into GodMode / ComponentOverride mutations.
 */

import { EnemyType, ENEMY_STATS, EnemyWeaponType, TILE_SIZE } from '@/config/GameConfig';
import { ComponentType, COMPONENT_DEFS, WeaponTarget } from '@/components/ComponentDefs';
import { ROBOT_TEMPLATES } from '@/config/RobotTemplates';
import {
  GOD,
  getEnemyTypeStatOverride,
  setEnemyTypeStatOverride,
  ENEMY_TYPE_STAT_OVERRIDES,
} from '@/config/GodMode';
import { getOverride, setOverride } from '@/config/ComponentOverrides';

// ── Axis enum ──
export enum BalanceAxis {
  HP = 'HP',
  DPS = 'DPS',
  SPEED = 'Speed',
  RANGE = 'Range',
  COST = 'Cost',
  ENERGY_SUSTAIN = 'EnergySustain',
  AOE_FACTOR = 'AoEFactor',
  POWER = 'Power',
}

export const AXIS_LABELS: Record<BalanceAxis, string> = {
  [BalanceAxis.HP]: 'HP',
  [BalanceAxis.DPS]: 'DPS',
  [BalanceAxis.SPEED]: 'Speed',
  [BalanceAxis.RANGE]: 'Range',
  [BalanceAxis.COST]: 'Cost',
  [BalanceAxis.ENERGY_SUSTAIN]: 'E.Sustain',
  [BalanceAxis.AOE_FACTOR]: 'AoE',
  [BalanceAxis.POWER]: 'Power',
};

// Axes that support dragging (direct stat mapping)
export const DRAGGABLE_AXES = new Set([
  BalanceAxis.HP, BalanceAxis.DPS, BalanceAxis.SPEED, BalanceAxis.RANGE, BalanceAxis.COST,
]);

// ── Unit data point ──
export interface BalanceUnit {
  id: string;
  name: string;
  isEnemy: boolean;
  isFlying: boolean;
  color: number;
  stats: Record<BalanceAxis, number>;
  isModified: boolean;
  enemyType?: EnemyType;
  templateIndex?: number;
}

const FLYING_ENEMIES = new Set([EnemyType.DRONE, EnemyType.BOMBER, EnemyType.WASP, EnemyType.SPECTER]);

// ── Compute all units ──
export function computeAllUnits(): BalanceUnit[] {
  return [...computeEnemies(), ...computeRobots()];
}

function computeEnemies(): BalanceUnit[] {
  const units: BalanceUnit[] = [];
  for (const etype of Object.values(EnemyType)) {
    const s = ENEMY_STATS[etype];
    if (!s) continue;
    const typeOv = getEnemyTypeStatOverride(etype);
    const isFlying = FLYING_ENEMIES.has(etype);

    // HP (with global + per-type mults)
    const rawHP = s.hp * GOD.enemyHpMult * typeOv.hpMult;

    // Speed (tiles/s → px/s for display, but show as tiles/s)
    const rawSpeed = s.speed * GOD.enemySpeedMult * typeOv.speedMult;

    // DPS from weapons
    let weaponDPS = 0;
    let maxRange = 0;
    let aoeFactor = 1;
    for (const w of s.weapons) {
      let dps = w.damage / w.cooldown;
      if (w.type === EnemyWeaponType.BURST && w.burstCount) {
        dps = (w.damage * w.burstCount) / w.cooldown;
      }
      if (w.type === EnemyWeaponType.FLAMETHROWER) aoeFactor = Math.max(aoeFactor, 1.3);
      weaponDPS += dps;
      if (w.range > maxRange) maxRange = w.range;
    }
    // Melee DPS
    const meleeDPS = s.baseDamage / s.meleeCooldown;
    const totalDPS = (weaponDPS + meleeDPS) * GOD.enemyDamageMult * typeOv.damageMult;

    // Range (with per-type override)
    const range = maxRange * (GOD.enemyWeaponRangeMult ?? 1) * typeOv.rangeMult;

    // Cost = reward (inverse for enemies — higher reward = more "expensive" to field)
    const cost = s.reward;

    // Energy sustain: enemies have infinite energy, so 1.0
    const energySustain = 1.0;

    const power = Math.sqrt(Math.max(1, rawHP) * Math.max(0.1, totalDPS));
    const isModified = etype in ENEMY_TYPE_STAT_OVERRIDES;

    units.push({
      id: `enemy_${etype}`,
      name: s.label,
      isEnemy: true,
      isFlying,
      color: s.color,
      stats: {
        [BalanceAxis.HP]: rawHP,
        [BalanceAxis.DPS]: totalDPS,
        [BalanceAxis.SPEED]: rawSpeed,
        [BalanceAxis.RANGE]: range,
        [BalanceAxis.COST]: cost,
        [BalanceAxis.ENERGY_SUSTAIN]: energySustain,
        [BalanceAxis.AOE_FACTOR]: aoeFactor,
        [BalanceAxis.POWER]: power,
      },
      isModified,
      enemyType: etype,
    });
  }
  return units;
}

function computeRobots(): BalanceUnit[] {
  const units: BalanceUnit[] = [];
  const templates = ROBOT_TEMPLATES;

  for (let ti = 0; ti < templates.length; ti++) {
    const tpl = templates[ti];
    let totalHP = 0;
    let totalMass = 0;
    let weaponDPS = 0;
    let maxSensorRange = 0;
    let maxWeaponRange = 0;
    let hasStabLegs = false;
    let isFlying = false;
    let aoeFactor = 1;
    let totalForce = 0;
    let energyCapacity = 0;
    let energyDrain = 0;
    let hasRecharge = false;
    let totalCost = 0;
    let anyOverride = false;

    for (const c of tpl.components) {
      const def = COMPONENT_DEFS[c.type];
      if (!def) continue;
      totalHP += def.hp;
      totalMass += def.mass;
      totalCost += def.cost;
      if (def.energy > 0) energyCapacity += def.energy;
      if (def.energy < 0) energyDrain += Math.abs(def.energy);

      if (c.type === ComponentType.STAB_LEGS) hasStabLegs = true;
      if (c.type === ComponentType.WING || c.type === ComponentType.TURBINE) isFlying = true;
      if (c.type === ComponentType.RECHARGE_MODULE) hasRecharge = true;

      // Check for overrides
      const ov = getOverride(c.type, 0);
      if (ov && Object.keys(ov).length > 0) anyOverride = true;

      // Weapon DPS
      if (def.damage > 0 && def.fireRate > 0) {
        let dps = def.damage * def.fireRate;
        if (def.salvoCount > 0) dps = def.damage * def.salvoCount * def.fireRate;
        weaponDPS += dps;

        if (def.weaponRange > maxWeaponRange) maxWeaponRange = def.weaponRange;
        if (c.type === ComponentType.FLAMETHROWER) aoeFactor = Math.max(aoeFactor, 1.3);
        if (c.type === ComponentType.CANNON_HEAVY) aoeFactor = Math.max(aoeFactor, 1.15);
        if (c.type === ComponentType.MINE_LAYER) aoeFactor = Math.max(aoeFactor, 1.25);
        if (c.type === ComponentType.ROCKET_POD) aoeFactor = Math.max(aoeFactor, 1.2);
      }

      if (def.sensorRange > maxSensorRange) maxSensorRange = def.sensorRange;
      if (def.force > 0) totalForce += def.force;
    }

    // Speed (tiles/s)
    const speed = totalMass > 0 ? (totalForce / totalMass) : 0;

    // Range = max(sensor, weapon) — effective engagement range
    const range = Math.max(maxSensorRange, maxWeaponRange);

    // Energy sustainability factor
    let energySustain = 1;
    if (!hasRecharge && energyDrain > 0 && energyCapacity > 0) {
      const opTime = energyCapacity / energyDrain;
      energySustain = Math.min(1, opTime / 60);
    } else if (hasRecharge) {
      energySustain = 1;
    }

    const power = Math.sqrt(Math.max(1, totalHP) * Math.max(0.1, weaponDPS * aoeFactor * energySustain));

    const typeLabel = isFlying ? '✈' : (totalForce > 0 ? '⚙' : '🔧');
    units.push({
      id: `robot_${tpl.name}`,
      name: tpl.name,
      isEnemy: false,
      isFlying,
      color: parseInt(tpl.color.replace('#', ''), 16),
      stats: {
        [BalanceAxis.HP]: totalHP,
        [BalanceAxis.DPS]: weaponDPS,
        [BalanceAxis.SPEED]: speed,
        [BalanceAxis.RANGE]: range,
        [BalanceAxis.COST]: totalCost,
        [BalanceAxis.ENERGY_SUSTAIN]: energySustain,
        [BalanceAxis.AOE_FACTOR]: aoeFactor,
        [BalanceAxis.POWER]: power,
      },
      isModified: anyOverride,
      templateIndex: ti,
    });
  }
  return units;
}

// ── Reverse-mapping: set robot template stat from chart drag ──
// Scales component-level stats proportionally via ComponentOverrides.
export function setRobotStatFromAxis(templateIndex: number, axis: BalanceAxis, newValue: number): void {
  const templates = ROBOT_TEMPLATES;
  const tpl = templates[templateIndex];
  if (!tpl) return;

  // Compute current value for this axis to get the scale ratio
  let currentValue = 0;
  switch (axis) {
    case BalanceAxis.HP: {
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (def) currentValue += def.hp;
      }
      if (currentValue <= 0) return;
      const ratio = newValue / currentValue;
      // Scale each component's HP proportionally
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def || def.hp <= 0) continue;
        const ov = getOverride(c.type, 0);
        setOverride(c.type, 0, { ...ov, hp: Math.round(def.hp * ratio) });
      }
      break;
    }
    case BalanceAxis.DPS: {
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def || def.damage <= 0 || def.fireRate <= 0) continue;
        let dps = def.damage * def.fireRate;
        if (def.salvoCount > 0) dps = def.damage * def.salvoCount * def.fireRate;
        currentValue += dps;
      }
      if (currentValue <= 0) return;
      const ratio = newValue / currentValue;
      // Scale weapon damage proportionally
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def || def.damage <= 0) continue;
        const ov = getOverride(c.type, 0);
        setOverride(c.type, 0, { ...ov, damage: Math.round(def.damage * ratio * 10) / 10 });
      }
      break;
    }
    case BalanceAxis.SPEED: {
      let totalForce = 0;
      let totalMass = 0;
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def) continue;
        totalMass += def.mass;
        if (def.force > 0) totalForce += def.force;
      }
      currentValue = totalMass > 0 ? totalForce / totalMass : 0;
      if (currentValue <= 0 || totalForce <= 0) return;
      // speed = force/mass → to change speed, scale force
      const ratio = newValue / currentValue;
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def || def.force <= 0) continue;
        const ov = getOverride(c.type, 0);
        setOverride(c.type, 0, { ...ov, force: Math.round(def.force * ratio) });
      }
      break;
    }
    case BalanceAxis.RANGE: {
      let maxSensor = 0;
      let maxWeapon = 0;
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def) continue;
        if (def.sensorRange > maxSensor) maxSensor = def.sensorRange;
        if (def.weaponRange > maxWeapon) maxWeapon = def.weaponRange;
      }
      currentValue = Math.max(maxSensor, maxWeapon);
      if (currentValue <= 0) return;
      const ratio = newValue / currentValue;
      // Scale both sensor and weapon ranges
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def) continue;
        const ov = getOverride(c.type, 0);
        const updates: any = { ...ov };
        if (def.sensorRange > 0) updates.sensorRange = Math.round(def.sensorRange * ratio);
        if (def.weaponRange > 0) updates.weaponRange = Math.round(def.weaponRange * ratio);
        if (updates.sensorRange || updates.weaponRange) setOverride(c.type, 0, updates);
      }
      break;
    }
    case BalanceAxis.COST: {
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (def) currentValue += def.cost;
      }
      if (currentValue <= 0) return;
      const ratio = newValue / currentValue;
      for (const c of tpl.components) {
        const def = COMPONENT_DEFS[c.type];
        if (!def || def.cost <= 0) continue;
        const ov = getOverride(c.type, 0);
        setOverride(c.type, 0, { ...ov, cost: Math.round(def.cost * ratio) });
      }
      break;
    }
    default:
      break;
  }
}

// ── Reverse-mapping: set enemy stat from chart drag ──
export function setEnemyStatFromAxis(type: EnemyType, axis: BalanceAxis, newValue: number): void {
  const s = ENEMY_STATS[type];
  if (!s) return;
  const current = getEnemyTypeStatOverride(type);

  switch (axis) {
    case BalanceAxis.HP: {
      const baseHP = s.hp * GOD.enemyHpMult;
      if (baseHP > 0) {
        setEnemyTypeStatOverride(type, { ...current, hpMult: newValue / baseHP });
      }
      break;
    }
    case BalanceAxis.DPS: {
      // Compute base DPS without per-type override
      let baseDPS = 0;
      for (const w of s.weapons) {
        let dps = w.damage / w.cooldown;
        if (w.type === EnemyWeaponType.BURST && w.burstCount) dps = (w.damage * w.burstCount) / w.cooldown;
        baseDPS += dps;
      }
      baseDPS += s.baseDamage / s.meleeCooldown;
      baseDPS *= GOD.enemyDamageMult;
      if (baseDPS > 0) {
        setEnemyTypeStatOverride(type, { ...current, damageMult: newValue / baseDPS });
      }
      break;
    }
    case BalanceAxis.SPEED: {
      const baseSpeed = s.speed * GOD.enemySpeedMult;
      if (baseSpeed > 0) {
        setEnemyTypeStatOverride(type, { ...current, speedMult: newValue / baseSpeed });
      }
      break;
    }
    case BalanceAxis.RANGE: {
      let maxRange = 0;
      for (const w of s.weapons) { if (w.range > maxRange) maxRange = w.range; }
      const baseRange = maxRange * (GOD.enemyWeaponRangeMult ?? 1);
      if (baseRange > 0) {
        setEnemyTypeStatOverride(type, { ...current, rangeMult: newValue / baseRange });
      }
      break;
    }
    default:
      // Non-draggable axes: no-op
      break;
  }
}
